/**
 * <TrendDelta> — F044 §9.1. `delta === 0` renders grey "+0" (`TrendDirection.None`) — NEVER green or
 * red on a null trend, regardless of the metric's `higherIsBetter` polarity. Otherwise polarity-aware.
 */
import type { FC } from 'react';
import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { ArrowDown12Filled, ArrowUp12Filled, Subtract12Filled } from '@fluentui/react-icons';
import { colorIntentToken, resolveTrend, trendColorIntent } from './vocabulary';

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
});

export interface TrendDeltaProps {
  /** The raw signed change (e.g. +3, -1, 0). */
  delta: number;
  higherIsBetter: boolean;
  /** Optional formatter, e.g. `(d) => `${d}%`` — defaults to a signed integer. */
  format?: (delta: number) => string;
}

export const TrendDelta: FC<TrendDeltaProps> = ({ delta, higherIsBetter, format }) => {
  const styles = useStyles();
  const direction = resolveTrend(delta, higherIsBetter);
  const { foreground } = colorIntentToken(trendColorIntent(direction));
  const label = format ? format(delta) : `${delta >= 0 ? '+' : ''}${delta}`;

  const Icon = direction === 'None' ? Subtract12Filled : delta > 0 ? ArrowUp12Filled : ArrowDown12Filled;

  return (
    <span className={styles.root} style={{ color: foreground }}>
      <Icon />
      <Text weight="semibold" style={{ color: foreground }}>
        {label}
      </Text>
    </span>
  );
};
